import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { toCheckoutData, VendorRequestType } from '@leanpay/models'
import { checkoutService, logoutUser } from '@leanpay/services'

import { queryClient } from './app'
import { useAppStore } from './app-store'
import {
    useGetSessionInfo,
    useGetVendorTokenCheckout,
    useValidateVendorProduct,
} from 'library/hooks/query-hooks'
import { useRouteTracker } from 'library/hooks/use-step-tracker'
import { isStandalonePublicRoute } from 'library/utils'

import SplashScreen from './markets/rom/splash-screen/splash-screen'

const AppInit = ({ children }: { children: ReactNode }) => {
    const params = new URLSearchParams(window.location.search)
    const location = useLocation()
    const navigate = useNavigate()
    const { data: sessionInfoData, refetch: refetchSessionInfo } = useGetSessionInfo()
    const { mutateAsync: mutateGetVendorTokenCheckout } = useGetVendorTokenCheckout()
    const { mutateAsync: mutateValidateVendorProduct } = useValidateVendorProduct()

    const {
        setVendorToken,
        setCheckoutData,
        setIsAuthenticated,
        setVendor,
        setProductCode,
        setValidateVendorData,
        validateVendorData,
        lastRecordedRoute,
        canAccessRoute,
        resetStore,
        vendorToken,
    } = useAppStore()

    const isInitialLoad = useRef(true)
    const [showSplashScreen, setShowSplashScreen] = useState(false)
    const [navigateToIntro, setNavigateToIntro] = useState(false)

    useRouteTracker()

    const handleGetVendorToken = async (token: string, vendorId: string) => {
        setVendor(vendorId)
        try {
            const vendorInitApiData = await mutateGetVendorTokenCheckout(token)
            setVendorToken(vendorInitApiData.token)
            await refetchSessionInfo()

            await checkoutService.getAuthorizationCodeStatus(vendorInitApiData.token)
            setCheckoutData(
                toCheckoutData({
                    token: vendorInitApiData.token,
                    isInStore: vendorInitApiData.inStoreFlag,
                    vendorRequestType: vendorInitApiData.vendorRequestType,
                    customerLanguage: vendorInitApiData.customerLanguage,
                    vendorName: vendorInitApiData.vendorName,
                }),
            )

            navigate('/request-otp', {
                replace: true,
                state: {
                    hasGeneratedAuthorizationCode: true,
                },
            })
            return
        } catch {
            navigate('/checkout-error', { state: { error: 'DATA_NOT_VALID' }, replace: true })
            return
        }
    }

    const handleValidateVendorProduct = async () => {
        // Clear stale state from any previous flow
        resetStore()
        logoutUser()
        queryClient.clear()

        const vendor = params.get('vendor')
        const vendorId = params.get('vendorId')
        const productCode = params.get('productCode')
        const token = params.get('token')

        if (token && vendorId) return handleGetVendorToken(token, vendorId)
        if (vendor) {
            try {
                const vendorResponse = await mutateValidateVendorProduct({
                    vendor,
                    productCode: productCode ?? undefined,
                    requestType: VendorRequestType.SELF_CHECKOUT,
                })
                setValidateVendorData(vendorResponse)
                setVendor(vendor)
                setProductCode(productCode ?? '')

                setShowSplashScreen(true)
                const timeoutId = setTimeout(() => {
                    setShowSplashScreen(false)
                    setNavigateToIntro(true)
                }, 2000)
                return () => clearTimeout(timeoutId)
            } catch {
                navigate('/checkout-error', { state: { error: 'DATA_NOT_VALID' }, replace: true })
                return
            }
        } else {
            navigate('/checkout-error', { state: { error: 'DATA_NOT_VALID' }, replace: true })
        }
    }

    useEffect(() => {
        if (location.pathname === '/' && !isStandalonePublicRoute) {
            void handleValidateVendorProduct()
        }
    }, [location.pathname])

    useEffect(() => {
        if (sessionInfoData) {
            setIsAuthenticated(true)
        }
    }, [sessionInfoData])

    useEffect(() => {
        if (navigateToIntro) {
            navigate('/intro', { replace: true })
        }
    }, [navigateToIntro])

    // Route validation on initial load/refresh
    useEffect(() => {
        if (isInitialLoad.current && lastRecordedRoute && location.pathname !== '/') {
            isInitialLoad.current = false

            if (!canAccessRoute(location.pathname)) {
                const handleInvalidAccess = async () => {
                    try {
                        if (vendorToken) {
                            await checkoutService.invalidateVendorRequest({ token: vendorToken })
                        }
                    } catch {
                        console.error('Failed to invalidate vendor request')
                    } finally {
                        resetStore()
                        logoutUser()
                        navigate('/checkout-error', {
                            state: { error: 'DATA_NOT_VALID' },
                            replace: true,
                        })
                    }
                }
                void handleInvalidAccess()
            }
        }
    }, [location.pathname, lastRecordedRoute, canAccessRoute, vendorToken, resetStore, navigate])

    if (showSplashScreen) {
        return <SplashScreen validateVendorData={validateVendorData} />
    }

    return children
}

export default AppInit
